<template>
  <div>
    <tool-bar
        :title="toolbarTitle"
        :right-menu-actions="toolbarActionsList"
        @onActionSelected="onToolbarActionSelected"
        :showSubTitle="false"
        :showLenderCobrandingData="false"
    ></tool-bar>

    <loading :loading="loading"></loading>

    <error-alert-item
        :error-alert-event="errorAlertEvent"
        :network-error="networkError"
        @resetErrorAlertEvent="errorAlertEvent=false"
    ></error-alert-item>

    <radio-options-dialog
        select-option-label="Applicant Type"
        :show="showAssociatedApplicantTypeOptionDialog"
        :radio-options="getAssociatedEntityTypes"
        @onDismissClicked="showAssociatedApplicantTypeOptionDialog=false"
        @onOptionSelected="onAssociatedApplicantTypeSelected"
    ></radio-options-dialog>

    <home-loans-provider class="margin-top-20" ref="homeLoansProvider"></home-loans-provider>

    <add-button-dialog
        :show="allowAssociatedLoanCreation"
        @onAddButtonClicked="onApplyNewLoan"
    ></add-button-dialog>

  </div>
</template>

<script>

import LoanScopeDataHolder from "@/data/model/LoanScopeDataHolder";
import ActivityRedirectItem from "../layout/ActivityRedirectItem";
import ErrorAlertItem from "../layout/ErrorAlertItem";
import Loading from "../layout/Loading";
import ActivityNavigator, { Activities, ActivitiesDescription } from "@/utils/ActivityNavigator";
import HomeLoansProvider from "@/components/providers/HomeLoansProvider";
import { AssociatedEntityType, Constants } from "@/components/Constants";
import ToolBar from "@/components/layout/ToolBar";
import { ToolbarMenuItems } from "@/utils/ToolbarMenuItems";
import AssociatedEntityDataHolder from "@/data/model/AssociatedEntityDataHolder";
import AddButtonDialog from "@/components/dialogs/AddButtonDialog";
import EzUtils from "@/utils/EzUtils";
import DataSource from "@/data/DataSource";
import { DataTables } from "@/utils/DataTables";
import { LoanStatus } from "@/utils/LoanStatusDetails";
import RadioOptionsDialog from "@/components/dialogs/RadioOptionsDialog";

export default {
  name: "AssociatedEntitiesHomeActivity",
  components: {
    ActivityRedirectItem,
    HomeLoansProvider,
    ToolBar,
    AddButtonDialog,
    ErrorAlertItem,
    Loading,
    RadioOptionsDialog
  },

  data: () => ({
    loading: false,
    errorAlertEvent: false,
    networkError: {},
    showAssociatedApplicantTypeOptionDialog:false
  }),

  methods: {
    showError(error) {
      this.loading = false;
      this.networkError = error;
      this.errorAlertEvent = true;
    },

    onToolbarActionSelected(selectedAction) {
      if (ToolbarMenuItems.GO_TO_PARENT_LOAN === selectedAction) {
        ActivityNavigator.navigateTo(Activities.LOAN_DETAILS);
      }
    },

    onApplyNewLoan() {
      if (EzUtils.isNotEmpty(AssociatedEntityDataHolder.getAssociatedEntityTypes())) {
        this.showAssociatedApplicantTypeOptionDialog = true;
      }
    },

    onAssociatedApplicantTypeSelected(applicantType) {
      this.showAssociatedApplicantTypeOptionDialog = false;

      const rawApplicantType = Constants.getRawValueForAssociatedEntityType(applicantType);

      LoanScopeDataHolder.setParentLoanId(AssociatedEntityDataHolder.getParentLoanId());
      LoanScopeDataHolder.setLoanAmount(AssociatedEntityDataHolder.getParentLoanPrice());
      LoanScopeDataHolder.setAssociatedEntityType(rawApplicantType);
      LoanScopeDataHolder.setAssociatedEntityConfig(
          AssociatedEntityDataHolder.getAssociatedEntityConfig()
      );
      LoanScopeDataHolder.setParentApplicantType(
          AssociatedEntityDataHolder.getParentApplicantType()
      );

      ActivityNavigator.navigateTo(Activities.CUSTOMER_PHONE_NUMBER);
    },

    fetchAssociatedEntities() {
      if (EzUtils.isValidId(AssociatedEntityDataHolder.getParentLoanId())) {
        this.loading = true;
        this.fetchAssociatedEntitiesWithTypes(
            AssociatedEntityDataHolder.getParentLoanId(),
            AssociatedEntityDataHolder.getAssociatedEntityTypes(),
            true,
            [LoanStatus.ABANDONED]
        )
        .then(associatedEntitiesRes => {
          this.loading = false;
          if (associatedEntitiesRes.errorOccurred) {
            this.showError(associatedEntitiesRes.data);
          } else {
            this.$refs.homeLoansProvider.fetchLoansExternalMethod(
                AssociatedEntityDataHolder.getHeading(),
                false,
                associatedEntitiesRes.data
            );
          }
        })
      }
    },

    fetchAssociatedEntitiesWithTypes(
        loanId, associatedEntityTypes, validAssociatedLoanId, excludedStatus
    ) {
      return new Promise(resolve => {
        const returnResponse = [];
        if (EzUtils.isValidId(loanId) && EzUtils.isNotEmpty(associatedEntityTypes)) {
          DataSource.getDataTableInfo(DataTables.ASSOCIATED_ENTITIES, loanId)
          .then(associatedEntitiesRes => {
            if (associatedEntitiesRes.errorOccurred) {
              resolve(associatedEntitiesRes);
            } else {
              const associatedEntities = associatedEntitiesRes.data;
              if (EzUtils.isNotEmpty(associatedEntities)) {
                associatedEntities.forEach(associatedEntity => {
                  if (EzUtils.isNotEmpty(associatedEntity["associated_entity_type"])
                      && associatedEntityTypes.includes(associatedEntity["associated_entity_type"])
                      && (!validAssociatedLoanId
                          || EzUtils.isValidId(associatedEntity["associated_loan_id"])
                      )
                      && (EzUtils.isEmpty(excludedStatus)
                          || EzUtils.isEmpty(associatedEntity["status"])
                          || !excludedStatus.includes(associatedEntity["status"])
                      )
                  ) {
                    returnResponse.push(associatedEntity);
                  }
                })
                resolve(DataSource.onResponse(returnResponse));
              } else {
                resolve(DataSource.onResponse(returnResponse));
              }
            }
          })
        } else {
          resolve(DataSource.onResponse(returnResponse));
        }
      })
    }
  },

  created() {
    LoanScopeDataHolder.clearLoanScopeData();
  },

  mounted() {
    this.fetchAssociatedEntities();
  },

  beforeRouteLeave(to, from, next) {
    if (to
        && to.name
        && (ActivitiesDescription.LOAN_DETAILS.name === to.name
            || ActivitiesDescription.ASSOCIATED_ENTITY.name === to.name)
    ) {
      LoanScopeDataHolder.setLoanId(AssociatedEntityDataHolder.getParentLoanId());
    }

    //do not remove below code, otherwise navigation will fail
    next(vm => {
    })
  },

  computed: {
    toolbarActionsList() {
      return [ToolbarMenuItems.GO_TO_PARENT_LOAN];
    },

    allowAssociatedLoanCreation() {
      return AssociatedEntityDataHolder.isAllowAssociatedLoanCreation();
    },

    toolbarTitle() {
      return AssociatedEntityDataHolder.getToolbarTitle();
    },

    getAssociatedEntityTypes() {
      return AssociatedEntityDataHolder.getAssociatedEntityTypes();
    }
  }
}
</script>

<style scoped></style>
